import React, { useState, useEffect } from 'react';
import { apiFetch, getApiBaseUrl } from '../../utils/apiConfig';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  Tooltip,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../CentralSnackbar';
import TenantMultiSelect from '../common/TenantMultiSelect';
import HL7MappingDialog from './HL7MappingDialog';


const HL7SendNodes = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null
  });

  // Mapping Dialog State
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    ae_title: '',
    ip_address: '',
    port: '',
    is_active: true,
    tenants: '',
    mapping: null,
    cda_enabled: false
  });

  useEffect(() => {
    fetchNodes();
  }, []);

  const fetchNodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/hl7-send-nodes/`);
      if (response.ok) {
        const data = await response.json();
        setNodes(data);
      } else {
        throw new Error('Failed to fetch HL7 send nodes');
      }
    } catch (error) {
      console.error('Error fetching HL7 send nodes:', error);
      showError(t('hl7SendNodes.snackbar.loadError', 'Fehler beim Laden der HL7 Sendeknoten'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectNode = (nodeId) => {
    setSelectedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNodes.length === nodes.length) {
      setSelectedNodes([]);
    } else {
      setSelectedNodes(nodes.map(node => node.id));
    }
  };

  const handleEditSelected = () => {
    if (selectedNodes.length === 1) {
      const node = nodes.find(n => n.id === selectedNodes[0]);
      handleOpenDialog(node);
    }
  };

  const handleTestSelected = () => {
    if (selectedNodes.length === 1) {
      handleTestConnection(selectedNodes[0]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNodes.length === 0) return;
    
    let description;
    if (selectedNodes.length === 1) {
      const selectedNode = nodes.find(node => node.id === selectedNodes[0]);
      description = t('hl7SendNodes.confirmDelete.description', { 
        defaultValue: 'Möchten Sie den HL7 Sendeknoten "{{name}}" wirklich löschen?',
        name: selectedNode?.name || 'Unknown' 
      });
    } else {
      description = t('hl7SendNodes.confirmDelete.descriptionMultiple', { 
        defaultValue: 'Möchten Sie {{count}} HL7 Sendeknoten wirklich löschen?',
        count: selectedNodes.length 
      });
    }

    setConfirmDelete({
      open: true,
      title: t('hl7SendNodes.confirmDelete.title', 'HL7 Sendeknoten löschen'),
      description: description,
      onConfirm: async () => {
        try {
          const deletePromises = selectedNodes.map(nodeId =>
            fetch(`${getApiBaseUrl()}/hl7-send-nodes/${nodeId}`, {
              method: 'DELETE'
            })
          );

          const results = await Promise.all(deletePromises);
          const allSuccessful = results.every(response => response.ok);

          if (allSuccessful) {
            showSuccess(selectedNodes.length === 1 
              ? t('hl7SendNodes.snackbar.deleteSuccess', 'HL7 Sendeknoten erfolgreich gelöscht')
              : `${selectedNodes.length} HL7 Sendeknoten erfolgreich gelöscht`);
            setSelectedNodes([]);
            fetchNodes();
          } else {
            throw new Error('Some deletions failed');
          }
        } catch (error) {
          console.error('Error deleting HL7 send nodes:', error);
          showError(t('hl7SendNodes.snackbar.deleteError', 'Fehler beim Löschen des HL7 Sendeknotens'));
        }
      }
    });
  };

  const handleOpenDialog = (node = null) => {
    if (node) {
      setEditingNode(node);
      setFormData({
        name: node.name,
        ae_title: node.ae_title,
        ip_address: node.ip_address,
        port: node.port.toString(),
        is_active: node.is_active,
        tenants: node.tenants || '',
        mapping: node.mapping || null,
        cda_enabled: node.cda_enabled || false
      });
    } else {
      setEditingNode(null);
      setFormData({
        name: '',
        ae_title: '',
        ip_address: '',
        port: '',
        is_active: true,
        tenants: '',
        mapping: null,
        cda_enabled: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNode(null);
    setFormData({
      name: '',
      ae_title: '',
      ip_address: '',
      port: '',
      is_active: true,
      tenants: '',
      mapping: null,
      cda_enabled: false
    });
  };

  const handleMappingSave = (mappingData) => {
    setFormData(prev => ({
      ...prev,
      mapping: mappingData
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const url = editingNode 
        ? `${getApiBaseUrl()}/hl7-send-nodes/${editingNode.id}`
        : `${getApiBaseUrl()}/hl7-send-nodes/`;
      
      const method = editingNode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          port: parseInt(formData.port)
        })
      });

      if (response.ok) {
        showSuccess(editingNode 
          ? t('hl7SendNodes.snackbar.updateSuccess', 'HL7 Sendeknoten erfolgreich aktualisiert')
          : t('hl7SendNodes.snackbar.createSuccess', 'HL7 Sendeknoten erfolgreich erstellt'));
        handleCloseDialog();
        fetchNodes();
      } else {
        throw new Error('Failed to save HL7 send node');
      }
    } catch (error) {
      console.error('Error saving HL7 send node:', error);
      showError(t('hl7SendNodes.snackbar.saveError', 'Fehler beim Speichern des HL7 Sendeknotens'));
    }
  };

  const handleTestConnection = async (nodeId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/hl7-send-nodes/${nodeId}/test`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        
        // Success/Error Snackbar basierend auf Backend-Response
        const node = nodes.find(n => n.id === nodeId);
        if (result.success) {
          showSuccess(t('hl7SendNodes.testSuccess', {
            defaultValue: 'HL7-Verbindung zu "{{nodeName}}" erfolgreich getestet',
            nodeName: node?.name || 'Unbekannt'
          }));
        } else {
          showError(t('hl7SendNodes.testFailed', {
            defaultValue: 'ERR-HL7-003: HL7-Verbindungstest zu "{{nodeName}}" fehlgeschlagen: {{error}}',
            nodeName: node?.name || 'Unbekannt',
            error: result.message
          }), 'ERR-HL7-003');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error testing HL7 connection:', error);
      
      // Error Snackbar mit Fehlercode
      const node = nodes.find(n => n.id === nodeId);
      showError(t('hl7SendNodes.testError', {
        defaultValue: 'ERR-HL7-004: Fehler beim Testen der HL7-Verbindung zu "{{nodeName}}"',
        nodeName: node?.name || 'Unbekannt'
      }), 'ERR-HL7-004');
    }
  };



  return (
    <Box>


      {/* Action Buttons - Always visible */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ mr: 2 }}>
            {selectedNodes.length > 0 
              ? t('hl7SendNodes.selectedNodes', { 
                  defaultValue: '{{count}} Knoten ausgewählt',
                  count: selectedNodes.length 
                })
              : t('hl7SendNodes.noNodesSelected', 'Keine Knoten ausgewählt')
            }
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('hl7SendNodes.actions.edit', 'Bearbeiten')}>
            <IconButton 
              size="small" 
              onClick={handleEditSelected}
              disabled={loading || selectedNodes.length !== 1}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('hl7SendNodes.testConnection', 'Verbindung testen')}>
            <IconButton 
              size="small" 
              onClick={handleTestSelected}
              disabled={loading || selectedNodes.length !== 1}
            >
              <TestIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('hl7SendNodes.actions.delete', 'Löschen')}>
            <IconButton 
              size="small" 
              onClick={handleDeleteSelected}
              disabled={loading || selectedNodes.length === 0}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ ml: 2, mr: 1 }} />
          <Box display="flex" alignItems="center" gap={1} flexGrow={1} justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              className="button"
            >
              {t('hl7SendNodes.add', 'Hinzufügen')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedNodes.length === nodes.length && nodes.length > 0}
                  indeterminate={selectedNodes.length > 0 && selectedNodes.length < nodes.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t('hl7SendNodes.table.name', 'Name')}</TableCell>
              <TableCell>{t('hl7SendNodes.table.aeTitle', 'AE Title')}</TableCell>
              <TableCell>{t('hl7SendNodes.table.ipAddress', 'IP-Adresse')}</TableCell>
              <TableCell>{t('hl7SendNodes.table.port', 'Port')}</TableCell>
              <TableCell>{t('hl7SendNodes.table.status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('hl7SendNodes.noNodes', 'Keine HL7 Sendeknoten konfiguriert')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              nodes.map((node) => (
                <TableRow key={node.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedNodes.includes(node.id)}
                      onChange={() => handleSelectNode(node.id)}
                    />
                  </TableCell>
                  <TableCell>{node.name}</TableCell>
                  <TableCell>{node.ae_title}</TableCell>
                  <TableCell>{node.ip_address}</TableCell>
                  <TableCell>{node.port}</TableCell>
                  <TableCell>
                    <Chip
                      label={node.is_active ? t('hl7SendNodes.status.active', 'Aktiv') : t('hl7SendNodes.status.inactive', 'Inaktiv')}
                      color={node.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Test Results Display */}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingNode ? t('hl7SendNodes.dialog.editTitle', 'HL7 Sendeknoten bearbeiten') : t('hl7SendNodes.dialog.createTitle', 'Neuen HL7 Sendeknoten erstellen')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('hl7SendNodes.form.name', 'Name')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            margin="normal"
            required
            helperText={t('hl7SendNodes.form.nameHelp', 'z.B. "JiveX Production"')}
          />
          <TextField
            fullWidth
            label={t('hl7SendNodes.form.aeTitle', 'AE Title / System Name')}
            value={formData.ae_title}
            onChange={(e) => handleInputChange('ae_title', e.target.value)}
            margin="normal"
            required
            helperText={t('hl7SendNodes.form.aeTitleHelp', 'z.B. "JIVEX"')}
          />
          <TextField
            fullWidth
            label={t('hl7SendNodes.form.ipAddress', 'IP-Adresse')}
            value={formData.ip_address}
            onChange={(e) => handleInputChange('ip_address', e.target.value)}
            margin="normal"
            required
            helperText={t('hl7SendNodes.form.ipAddressHelp', 'z.B. "192.168.1.100"')}
          />
          <TextField
            fullWidth
            label={t('hl7SendNodes.form.port', 'Port')}
            type="number"
            value={formData.port}
            onChange={(e) => handleInputChange('port', e.target.value)}
            margin="normal"
            required
            inputProps={{ min: 1, max: 65535 }}
            helperText={t('hl7SendNodes.form.portHelp', 'z.B. "3377" für JiveX HL7')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
              />
            }
            label={t('hl7SendNodes.form.active', 'Aktiv')}
            sx={{ mt: 2, mb: 2 }}
          />
          <TenantMultiSelect
            value={formData.tenants}
            onChange={(value) => handleInputChange('tenants', value)}
            label={t('hl7SendNodes.tenants', 'Verfügbare Tenants')}
            helperText={t('hl7SendNodes.tenantsHelp', 'Leer = für alle Tenants verfügbar')}
          />
          
          {/* HL7 Mapping Button */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setMappingDialogOpen(true)}
              className="button"
              fullWidth
            >
              {t('hl7SendNodes.configureMapping', 'HL7 Feldmapping konfigurieren')}
              {formData.mapping && (
                <Chip 
                  label={t('hl7SendNodes.mappingConfigured', 'Konfiguriert')} 
                  size="small" 
                  color="success" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Button>
            {formData.mapping && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('hl7SendNodes.mappingFieldsCount', '{{count}} Felder gemappt', { 
                  count: Object.keys(formData.mapping).length 
                })}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} className="button-cancel">
            {t('hl7SendNodes.dialog.cancel', 'Abbrechen')}
          </Button>
          <Button onClick={handleSubmit} variant="contained" className="button">
            {editingNode ? t('hl7SendNodes.dialog.update', 'Aktualisieren') : t('hl7SendNodes.dialog.create', 'Erstellen')}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ ...confirmDelete, open: false })}
      >
        <DialogTitle>{confirmDelete.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDelete.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ ...confirmDelete, open: false })} className="button-cancel">
            {t('hl7SendNodes.confirmDelete.cancel', 'Abbrechen')}
          </Button>
          <Button 
            onClick={() => {
              if (confirmDelete.onConfirm) confirmDelete.onConfirm();
              setConfirmDelete({ ...confirmDelete, open: false });
            }} 
            color="primary" 
            autoFocus
            className="button"
          >
            {t('hl7SendNodes.confirmDelete.confirm', 'Löschen')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* HL7 Mapping Dialog */}
      <HL7MappingDialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        onSave={handleMappingSave}
        initialMapping={formData.mapping}
      />
    </Box>
  );
};

export default HL7SendNodes;